import i18n from 'i18next';
import { useTranslation, initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import authEn from './auth-en.json';
import { languageDetectionOptions, normalizeLocale } from './language-detection';

// Auth strings live in the shared `translation` namespace under the `auth.*`
// prefix so they resolve from the same bundle the consuming app downloads from
// Lokalise (ari) and from the bundled English fallback below. The lib never
// owns a private namespace — that would strand non-English locales the app
// loads into `translation`.
let registered = false;

/**
 * Make the bundled English auth catalog resolvable on the global i18next
 * singleton. Idempotent and safe to call from render.
 *
 * - If an app already initialised i18next (ari, ari-cloud both do at bootstrap
 *   with `['localStorage','navigator']` detection), we only overlay the English
 *   bundle — without overwriting any app-provided keys — so first-visit browser
 *   detection that the app already performs governs the login language too.
 * - If no app has initialised i18next yet (lib used standalone / isolated
 *   tests), we self-init with the same detection order and English fallback so
 *   the login flow still auto-detects the browser language before auth.
 */
export function ensureAuthI18n(): void {
  if (registered) return;

  if (!i18n.isInitialized) {
    i18n
      .use(LanguageDetector)
      .use(initReactI18next)
      .init({
        fallbackLng: 'en',
        // Empty strings from in-review Lokalise locales must fall back to
        // English, mirroring the apps' convention — never render a blank label.
        returnEmptyString: false,
        nonExplicitSupportedLngs: true,
        interpolation: { escapeValue: false },
        // Normalize region tags (nb-NO/no -> nb, zh-CN -> zh, zh-HK -> yue,
        // generic xx-YY -> xx) so the pre-auth login flow matches the same
        // bundle the app would — see apps/ari/src/lib/i18n.ts.
        detection: { ...languageDetectionOptions, convertDetectedLanguage: normalizeLocale },
        resources: { en: { translation: authEn } },
        react: { useSuspense: false },
      });
  }

  // Overlay English without clobbering app keys (overwrite=false). deep=true so
  // nested auth.* keys merge into any existing `translation` bundle.
  i18n.addResourceBundle('en', 'translation', authEn, true, false);
  registered = true;
}

/**
 * `useTranslation` bound to the default `translation` namespace, with the
 * English auth bundle guaranteed present.
 */
export function useAuthTranslation() {
  ensureAuthI18n();
  return useTranslation();
}

export type AuthFlow = 'login' | 'signup' | 'forgotPassword';

export interface ProxyErrorBody {
  error?: string;
  message?: string;
}

const LOGIN_ERROR_KEYS: Record<string, string> = {
  invalid_request: 'auth.errors.emailPasswordRequired',
  invalid_credentials: 'auth.errors.invalidCredentials',
  too_many_attempts: 'auth.errors.tooManyAttempts',
  mfa_required: 'auth.errors.mfaRequired',
  upstream_timeout: 'auth.errors.upstreamTimeout',
};

const SIGNUP_ERROR_KEYS: Record<string, string> = {
  invalid_request: 'auth.errors.emailPasswordRequired',
  invalid_password: 'auth.errors.invalidPassword',
  upstream_timeout: 'auth.errors.upstreamTimeout',
};

const FLOW_FALLBACK_KEY: Record<AuthFlow, string> = {
  login: 'auth.errors.loginFailed',
  signup: 'auth.errors.signupFailed',
  forgotPassword: 'auth.errors.forgotFailed',
};

/**
 * Map a backend proxy error to a localized i18n key, entirely client-side.
 * `email_not_verified` is handled by the caller (routes to /verify-email)
 * before this is reached. A JSON-less or unmapped 5xx is a platform problem,
 * not a credential problem, so it surfaces as serviceUnavailable.
 */
export function mapAuthError(flow: AuthFlow, body: ProxyErrorBody, status: number): string {
  const table = flow === 'signup' ? SIGNUP_ERROR_KEYS : flow === 'login' ? LOGIN_ERROR_KEYS : {};
  if (body.error && table[body.error]) return table[body.error];
  if (status >= 500) return 'auth.errors.serviceUnavailable';
  return FLOW_FALLBACK_KEY[flow];
}
